import { AxiosError, AxiosResponse } from 'axios'
import { isNetworkError } from 'errors/handleError'
import { httpClient } from 'ServerAPI'

const uploadTraceLog = (...args: unknown[]) => {
  // eslint-disable-next-line no-console
  // console.log(...args)
}

export type FileType = 'media' | 'project'

type ResponseBody = {
  id: string
  folderId?: string
}

type UploadSessionParams<T> = T extends 'media' ? {
  folder?: string,
} : {
  fps?: number,
  // eslint-disable-next-line camelcase
  createFolder?: boolean
}

type Body = {
  name?: string,
  size?: number,
  absPathName?: string
}

export function createUploadSession<T extends FileType>(body: Body,
  type: T, params: UploadSessionParams<T> = {}, config = {}) {
  return httpClient.post<Body & UploadSessionParams<T>, AxiosResponse<ResponseBody>>(
    `/api/upload/${type}`, { ...body, ...params }, { ...config }
  )
}

type UploadFileRequestParams = RequestParams & {
  type: FileType
}

export function uploadFileRequest({ id,
  chunk, start, end,
  totalSize, type, controller = new AbortController() }: UploadFileRequestParams) {
  uploadTraceLog(`[uploadTrace] request id=${id} type=${type} bytes ${start}-${end}/${totalSize} size=${chunk.size}`)
  return httpClient.put(`/api/upload/${type}`, chunk, {
    signal: controller.signal,
    headers: {
      'Content-type': 'application/octet-stream',
      'Content-Range': `bytes ${start}-${end}/${totalSize}`,
    },
    params: {
      id,
    },
  })
}

export type RequestParams = {
  id: string,
  chunk: Blob,
  start: number,
  end: number,
  totalSize: number,
  controller?: AbortController
}

type UploadFileParams = {
  id: string,
  file: File & { path: string },
  request: (x: RequestParams) => Promise<AxiosResponse<unknown>>
  onFileUploaded?: () => void,
  onError?: (e?: AxiosError | Error) => void,
  controller?: AbortController
}

const NON_RETRIABLE_STATUSES = new Set([ 403 ])

const isNonRetriableError = (err: unknown) => {
  const status = (err as AxiosError)?.response?.status
  return status !== undefined && NON_RETRIABLE_STATUSES.has(status)
}

export const uploadFile = async ({
  id,
  file,
  request,
  onFileUploaded = () => {},
  onError = () => {},
  controller = new AbortController(),
  threads = 4, // 4 parts for parallel upload
  maxAttempts = 3,
}: UploadFileParams & { threads?: number, maxAttempts?: number }) => {
  const chunkSizeTable = [ 128 * 1024, 256 * 1024, 512 * 1024, 1024 * 1024, 2 * 1024 * 1024 ]
  const BLOCK_SIZE = 1024 * 1024 * 1024
  const totalSize = file.size
  // let uploadedSize = 0
  let errorOccurred = false
  let errorReported = false

  function cancel() {
    errorOccurred = true
    controller.signal.removeEventListener('abort', cancel)
  }

  controller.signal.addEventListener('abort', cancel)

  const uploadChunk = async (start: number, end: number) => {
    const chunk = file.slice(start, end)
    return request({
      id,
      chunk,
      start,
      end: end - 1,
      totalSize,
      controller,
    })
  }

  const uploadPart = async (partStart: number, partEnd: number) => {
    let chunkStart = partStart
    let currentChunkSizeTableIndex = 0
    let adjustChunkSize = true
    while (chunkStart < partEnd && !errorOccurred) {
      let retryCount = Math.max(1, maxAttempts)
      let error: Error | null = null
      while (retryCount) {
        try {
          error = null
          const chunkEnd = Math.min(chunkStart + chunkSizeTable[currentChunkSizeTableIndex], partEnd)
          uploadTraceLog(`[uploadTrace] chunk ${partStart}-${partEnd} ${chunkStart}-${chunkEnd} `
            + `size=${chunkSizeTable[currentChunkSizeTableIndex]} `
            + `index=${currentChunkSizeTableIndex} retry=${maxAttempts - retryCount + 1}`)
          // eslint-disable-next-line
          await uploadChunk(chunkStart, chunkEnd)
          chunkStart = chunkEnd
          retryCount = 0
          if (adjustChunkSize
            && currentChunkSizeTableIndex < (chunkSizeTable.length - 1)) {
            uploadTraceLog(`[uploadTrace] bump chunk size index ${currentChunkSizeTableIndex} `
              + `-> ${currentChunkSizeTableIndex + 1}`)
            currentChunkSizeTableIndex += 1
          }
        } catch (err) {
          if (controller.signal.aborted) {
            uploadTraceLog(`[uploadTrace] upload aborted at ${chunkStart}`)
            break
          }
          error = err instanceof Error ? err : new Error(String(err))
          if (isNonRetriableError(err)) {
            uploadTraceLog(`[uploadTrace] non-retriable error at ${chunkStart}: `, error)
            break
          }
          if (currentChunkSizeTableIndex > 0) {
            uploadTraceLog('[uploadTrace] drop chunk size index '
              + `${currentChunkSizeTableIndex} -> ${currentChunkSizeTableIndex - 1}`)
            currentChunkSizeTableIndex -= 1 // drop down to one step back and retry
          }
          adjustChunkSize = false // stop adjust if error happened
          if (isNetworkError(err)) {
            uploadTraceLog(`[uploadTrace] network error at ${chunkStart}, retrying: `, error)
            continue
          }
          uploadTraceLog(`[uploadTrace] retriable error at ${chunkStart}: `, error)
          retryCount -= 1
        }
        if (retryCount === 0) {
          break
        }
      }
      if (error) {
        errorOccurred = true
        controller.abort()
        uploadTraceLog(`[uploadTrace] giving up on part ${partStart}-${partEnd}, `
          + `errorReported=${errorReported}`, error)
        if (!errorReported) {
          errorReported = true
          onError(error)
        }
      }
    }
  }

  const uploadBlock = async (blockStart: number) => {
    const blockEnd = Math.min(blockStart + BLOCK_SIZE, totalSize)
    const blockSize = blockEnd - blockStart

    const threadCount = Math.min(threads, Math.ceil(blockSize / chunkSizeTable[0]))
    const threadPartSize = Math.ceil(blockSize / threadCount)
    uploadTraceLog(`[uploadTrace] block start=${blockStart} blockEnd=${blockEnd} `
      + `blockSize=${blockSize} threadCount=${threadCount} `
      + `threadPartSize=${threadPartSize}`)

    const threadPromises: Promise<void>[] = []

    for (let t = 0; t < threadCount; t++) {
      const start = blockStart + t * threadPartSize
      const end = Math.min(start + threadPartSize, blockEnd)
      uploadTraceLog(`[uploadTrace] thread ${t} part ${start}-${end}`)
      threadPromises.push(uploadPart(start, end))
    }

    await Promise.all(threadPromises)
    uploadTraceLog(`[uploadTrace] block done start=${blockStart}`)
  }

  const totalBlocks = Math.ceil(totalSize / BLOCK_SIZE)
  uploadTraceLog(`[uploadTrace] uploadFile start id=${id} name=${file.name} size=${totalSize} `
    + `threads=${threads} maxAttempts=${maxAttempts} totalBlocks=${totalBlocks} `
    + `fileSizeMB=${(totalSize / (1024 * 1024)).toFixed(2)}`)
  for (let blockIndex = 0; blockIndex < totalBlocks && !errorOccurred; blockIndex++) {
    const blockStart = blockIndex * BLOCK_SIZE
    uploadTraceLog(`[uploadTrace] block ${blockIndex} start=${blockStart} chunkSizeIndex=0`)
    // eslint-disable-next-line
    await uploadBlock(blockStart)
  }

  if (!errorOccurred) {
    uploadTraceLog(`[uploadTrace] uploadFile done id=${id}`)
    onFileUploaded()
  } else {
    uploadTraceLog(`[uploadTrace] uploadFile failed id=${id}`)
  }
}


export const cancelImport = async (url: string) => httpClient.delete(url)

export type ProjectUploadStage = 'upload' | 'unpack' | 'import_media' | 'ready' | 'error'

type ProgressResponseBody = {
    status: ProjectUploadStage,
    progress: number,
    error: string,
}

export const getProjectProgress = async (id: string, controller = new AbortController()) => {
  const { data } = await httpClient.get<ProgressResponseBody>(`/api/upload/project?id=${id}`, {
    signal: controller.signal,
  })

  if (data.error) {
    throw Error(data.error)
  }

  return { progress: data.progress, stage: data.status }
}
